import React, { useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Typography, Button, Alert, Divider, useTheme, useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { DataGrid } from "@mui/x-data-grid";

import { useColas } from "../hooks";
import { generateColaColumns } from "../helpers/columns";
import { rutaCola } from "../helpers/colas";
import { cerrarModal, crearModalInicial } from "../helpers/modales";
import { ESTADO_INICIAL_GRID } from "../helpers/gridProps";
import {
    DATA_GRID_SX,
    DATA_GRID_SLOT_PROPS,
    DATA_GRID_LOCALE_TEXT,
    DATA_GRID_PAGE_SIZE_OPTIONS,
} from "../../../components/ui/dataGridStyles";
import { ColasMobileList, ModalCola, ModalEliminarCola } from "../components";
import { BOTON_PRIMARIO_SX } from "../../../components/ui/buttonStyles";
import { StateContext } from "../../../components/context/StateProvider";
import Toasts from "../../../components/notificacion/Toasts";

const MODAL_INICIAL = crearModalInicial("cola");

/**
 * Página de gestión de colas de turnos (/colas): DataGrid en escritorio y
 * tarjetas en móvil. Orquestador: useColas aporta datos y mutaciones,
 * helpers/columns.js las columnas, ModalCola el alta/edición y
 * ModalEliminarCola la confirmación de borrado. El rol ADMIN se resuelve en
 * routes.js, no aquí.
 */
const ListaColas = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
    const navigate = useNavigate();
    const { state, updateState } = useContext(StateContext);
    const { colas, loading, guardando, error, crearCola, editarCola, eliminarCola } = useColas();

    const [modalCola, setModalCola] = useState(MODAL_INICIAL);
    const [modalEliminar, setModalEliminar] = useState(MODAL_INICIAL);
    const hayModalAbierto = modalCola.open || modalEliminar.open;

    const mostrarToast = useCallback(
        (mensaje, tipo) => {
            updateState({ toasts: [...state.toasts, Toasts.generateToast(mensaje, tipo)] });
        },
        [state.toasts, updateState]
    );

    const irA = useCallback(
        (accion) => (cola) => navigate(rutaCola(cola, accion)),
        [navigate]
    );
    const handleRegistro = useMemo(() => irA("registro"), [irA]);
    const handlePantalla = useMemo(() => irA("pantalla"), [irA]);
    const handleGestor = useMemo(() => irA("gestor"), [irA]);

    const handleAnadir = useCallback(() => setModalCola({ open: true, cola: null }), []);
    const handleEditar = useCallback((cola) => setModalCola({ open: true, cola }), []);
    const handleEliminar = useCallback((cola) => setModalEliminar({ open: true, cola }), []);
    const handleCerrarCola = useCallback(() => setModalCola(cerrarModal), []);
    const handleCerrarEliminar = useCallback(() => setModalEliminar(cerrarModal), []);

    // Si la API falla, relanza: el modal sigue abierto y la página avisa con un toast
    const handleGuardar = useCallback(
        async (nombre) => {
            const { cola } = modalCola;
            try {
                if (cola) await editarCola(cola.id, nombre);
                else await crearCola(nombre);
            } catch (err) {
                mostrarToast(err.message || "No se pudo guardar la cola.", "error");
                throw err;
            }
            mostrarToast(cola ? "Cola modificada correctamente" : "Cola creada correctamente", "success");
            setModalCola(cerrarModal);
        },
        [modalCola, crearCola, editarCola, mostrarToast]
    );

    const handleConfirmarEliminar = useCallback(async () => {
        try {
            await eliminarCola(modalEliminar.cola.id);
        } catch (err) {
            mostrarToast(err.message || "No se pudo eliminar la cola.", "error");
            return;
        }
        mostrarToast("Cola eliminada correctamente", "success");
        setModalEliminar(cerrarModal);
    }, [modalEliminar.cola, eliminarCola, mostrarToast]);

    const columns = useMemo(
        () =>
            generateColaColumns({
                onRegistro: handleRegistro,
                onPantalla: handlePantalla,
                onGestor: handleGestor,
                onEditar: handleEditar,
                onEliminar: handleEliminar,
            }),
        [handleRegistro, handlePantalla, handleGestor, handleEditar, handleEliminar]
    );

    return (
        <Box id="lista-colas" sx={{ width: "100%" }}>
            <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            justifyContent: "space-between",
                            alignItems: { xs: "stretch", sm: "flex-start" },
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography
                                variant={isMobile ? "h6" : "h5"}
                                sx={{ fontWeight: 700, color: "#212529", mb: 0.5, display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <ListAltIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                                Control de colas
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#6c757d" }}>
                                Crea y gestiona las colas del mostrador
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAnadir}
                            disabled={hayModalAbierto || guardando}
                            fullWidth={isMobile}
                            sx={BOTON_PRIMARIO_SX}
                        >
                            Añadir cola
                        </Button>
                    </Box>
                    {error && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Alert severity="error">
                                No se pudieron cargar las colas. Intenta nuevamente. ({error})
                            </Alert>
                        </>
                    )}
                </Box>

                <Box sx={{ px: isMobile ? 2 : 3, pb: 3 }}>
                    {isMobile ? (
                        <ColasMobileList
                            colas={colas}
                            loading={loading}
                            onRegistro={handleRegistro}
                            onPantalla={handlePantalla}
                            onGestor={handleGestor}
                            onEditar={handleEditar}
                            onEliminar={handleEliminar}
                        />
                    ) : (
                        <DataGrid
                            rows={colas}
                            columns={columns}
                            localeText={DATA_GRID_LOCALE_TEXT}
                            autoHeight
                            loading={loading}
                            disableRowSelectionOnClick
                            initialState={ESTADO_INICIAL_GRID}
                            pageSizeOptions={DATA_GRID_PAGE_SIZE_OPTIONS}
                            slotProps={DATA_GRID_SLOT_PROPS}
                            sx={DATA_GRID_SX}
                        />
                    )}
                </Box>
            </Card>

            <ModalCola
                show={modalCola.open}
                onHide={handleCerrarCola}
                onGuardar={handleGuardar}
                cola={modalCola.cola}
                guardando={guardando}
            />
            <ModalEliminarCola
                show={modalEliminar.open}
                onHide={handleCerrarEliminar}
                onConfirm={handleConfirmarEliminar}
                cola={modalEliminar.cola}
                eliminando={guardando}
            />
        </Box>
    );
};

export default ListaColas;
